import { useEffect, useRef } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import ROUTES from '~/shared/constants/routes.const'
import { PAYWALL_TRIGGER_ATTRIBUTE } from '~/subscription/hooks/useDeferredPaywallTrigger'
import { HorizontalNavItemType } from '~/types/layout'
import { selectHideHorizontalMenu, setHorizontalMenuHeightActionCreator } from '~/redux/slices/menu'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectIsChatAiOpen, setChatAiIsOpenActionCreator } from '~/redux/slices/chat-ai'
import { selectBootcampIsCollapsed } from '~/bootcamp/slices/bootcamp-page.slice'
import { useGetLeagueInfoForNav } from '~/hooks/useGetLeagueInfo'
import dashboardAnalytics from '~/analytics/dashboard/dashboardAnalytics'
import { Breakpoint, useCheckBreakpoint } from '~/utils'
import { selectPreviewSite } from '~/redux/slices/ui'
import { selectIsSubscriptionApplicable } from '~/redux/slices/subscription.slice'
import useGetCurriculumRedirectionUrl from '~/dashboard/hooks/useGetCurriculumRedirection'
import { getServerGradePageRoute } from '~/dashboard/hooks/useGetGradePageRoute'
import useSsrSafeValue from '~/hooks/useSsrSafeValue'
import { useGetCurrentGradeQuery } from '~/curriculum/apis/curriculum.api'
import { useGetUserChosenCurriculumCountryCode } from '~/curriculum/hooks/useGetCurriculumCountryCode'

import { HorizontalNavItem } from './HorizontalNavItem'

export function MenuHorizontal(): React.ReactElement {
  const { t } = useTranslation(['curriculum', 'home', 'navbar', 'imageAlt', 'menu', 'generics', 'dashboard', 'chatAi'])
  const router = useRouter()
  const bottomNavRef = useRef<HTMLDivElement>(null)
  const hideBottomMenu = useAppSelector(selectHideHorizontalMenu)
  const isCollapsed = useAppSelector(selectBootcampIsCollapsed)
  const isChatAiOpen = useAppSelector(selectIsChatAiOpen)
  const previewSite = useAppSelector(selectPreviewSite)
  const isSubscriptionApplicable = useAppSelector(selectIsSubscriptionApplicable)
  const authenticated = useAppSelector(isAuthenticated)

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(setHorizontalMenuHeightActionCreator(bottomNavRef.current?.offsetHeight || 0))
  }, [dispatch])

  const handleNavItemClick = (name: string): void => {
    if (name) dashboardAnalytics.handleMobileBottomMenuClick(name)
  }

  const handleCurriculumClick = (): void => {
    dashboardAnalytics.handleMobileBottomMenuClick('curriculum')
    router.push(curriculumRedirectionUrl)
  }

  const handleOnAiButtonClick = (): void => {
    if (authenticated) {
      dispatch(setChatAiIsOpenActionCreator(!isChatAiOpen))
    } else {
      router.push({ pathname: ROUTES.LOGIN, query: { from: router.asPath } })
    }
  }
  const handelLeagueOnClick = (): void => {
    dashboardAnalytics.handleDashboardItemViewClick('league')
    if (previewSite) {
      // eslint-disable-next-line fp/no-mutating-methods
      router.push(ROUTES.REGISTER)
      return
    } else {
      router.push(ROUTES.LEADERBOARD)
    }
  }
  const { leagueIconUrl, currentLeague, userCurrentRank } = useGetLeagueInfoForNav()
  const { data: gradeData } = useGetCurrentGradeQuery()
  const countryCode = useGetUserChosenCurriculumCountryCode()

  const curriculumRedirectionUrl = useGetCurriculumRedirectionUrl({
    specificGrade: gradeData?.grade ?? undefined,
    specificCountryCode: countryCode,
    specificSubjectOrder: gradeData?.subjectOrder ?? undefined,
    specificSubTopic: gradeData?.subTopic ?? undefined,
    specificSubTopicOrder: gradeData?.subTopicOrder ?? undefined,
  })
  // The URL depends on the curriculum cookie, so render the server's until mount. The click handler
  // still uses the real one.
  const curriculumHref = useSsrSafeValue(getServerGradePageRoute(), curriculumRedirectionUrl)

  const items: HorizontalNavItemType[] = [
    {
      iconSrc: 'icons/home.png',
      label: t('navbar:dashboard'),
      href: ROUTES.DASHBOARD,
      itemKey: 'dashboard',
      onClick: () => handleNavItemClick('home'),
      iconAlt: t('imageAlt:icons.dashboard'),
      iconHeight: 88,
      iconWidth: 100,
      show: true,
    },
    {
      iconSrc: 'icons/menu-curriculum.png',
      label: t('curriculum:dropdownTitle'),
      href: curriculumHref,
      itemKey: 'curriculum',
      onClick: handleCurriculumClick,
      iconAlt: t('home:products.curriculumTitle'),
      iconHeight: 88,
      iconWidth: 88,
      show: true,
    },
    {
      iconSrc: 'oracle-ai/globe.png',
      label: '',
      itemKey: 'globe-ai',
      onClick: () => handleOnAiButtonClick(),
      iconAlt: t('chatAi:globeAlt'),
      iconHeight: 88,
      iconWidth: 106,
      show: true,
      className: {
        wrapper: 'shrink-0 pt-3 px-0',
        icon: 'glow',
      },
    },
    {
      iconSrc: 'icons/stats-panel.png',
      label: t('dashboard:stats'),
      href: !authenticated && isSubscriptionApplicable ? ROUTES.SUBSCRIPTION_PAGE : ROUTES.STATISTICS,
      itemKey: 'stats',
      onClick: () => handleNavItemClick('stats'),
      iconAlt: t('imageAlt:icons.statistics'),
      iconHeight: 88,
      iconWidth: 106,
      show: true,
    },
    {
      itemKey: 'league',
      onClick: handelLeagueOnClick,
      iconSrc: leagueIconUrl,
      iconHeight: 88,
      iconWidth: 88,
      currentLeague,
      leagueRank: userCurrentRank,
      label: t('homebase:leagueNavButton'),
      show: true,
    },
  ]

  const isDesktop = useCheckBreakpoint({ gt: Breakpoint.LG })
  if (isDesktop) return <></>
  return (
    <nav
      {...{ [PAYWALL_TRIGGER_ATTRIBUTE]: 'true' }}
      className={`fixed flex md:hidden flex-col justify-top z-header left-0 h-40 right-0 ${
        !isCollapsed && hideBottomMenu ? '-bottom-40' : '-bottom-20'
      } rounded-t-3 border-0 bg-purple-abyss transition-all delay-150 duration-300 overflow-hidden `}
      ref={bottomNavRef}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <div className="flex justify-center h-20 w-full px-1">
        {items.map((item) => {
          return <HorizontalNavItem key={item.itemKey} {...item} />
        })}
      </div>
    </nav>
  )
}
